/**
 * Pure gameplay rules — single source of truth shared by both the engine
 * (apps/api/src/engine/) and the mobile client (apps/mobile/lib/).
 *
 * RULES:
 *  - No DB calls, no Fastify, no side effects.
 *  - All inputs are structural (minimal interfaces) so both the engine's
 *    internal GameState and the mobile's HydratedGameState can satisfy them
 *    without coupling to a specific concrete type.
 *  - These functions are the AUTHORITATIVE implementation.  Divergence between
 *    mobile and server silently 400s the placement-commit handler — that is why
 *    we have a single source of truth here.
 */

import type { Card, CardBonusSchedule } from './index.js'

// ─── Minimal structural inputs ────────────────────────────────────────────────
// Use these instead of importing full GameState / MapDef so callers on both
// sides don't need to satisfy every field.

export interface ReinforcementTerritory {
  id: string
  ownerId: string
}

export interface ReinforcementRegion {
  territoryIds: string[]
  bonus: number
}

/**
 * Die size for one side of one border. A bare number applies to BOTH attacking
 * and defending across it; the object form sets them separately (a missing key
 * falls back to the default six).
 */
export type BorderDice = number | { attack?: number; defend?: number }

export interface BorderReachTerritory {
  adjacencies: string[]
  oneWayBorders?: string[]
  borderDice?: Record<string, BorderDice>
}

/** Dice have six sides unless a border says otherwise. */
export const DEFAULT_DICE_SIDES = 6

export interface CappedTerritory {
  armies: number
  maxArmies?: number
}

/**
 * How many MORE armies this territory can take. `Infinity` when uncapped, which
 * is the default everywhere.
 *
 * Never negative: a stack already over its cap (a cap added to a live map, or
 * armies dealt at setup) simply has no room, rather than reporting a deficit
 * that callers would have to special-case.
 */
/**
 * The most a player can actually place this turn: their reinforcement budget,
 * or the total room their territories have left, whichever is smaller.
 *
 * Without this a troop cap can SOFT-LOCK a player. The placement routes require
 * the placed total to equal the budget exactly, so a player whose every
 * territory is at its cap could never commit a legal placement and could never
 * leave the phase. Capping the requirement instead simply drops the armies
 * there is no room for — the same thing that happens to a player who runs out
 * of anywhere to put them.
 */
export function placeableArmies(budget: number, owned: readonly CappedTerritory[]): number {
  let room = 0
  for (const t of owned) {
    room += armyHeadroom(t)
    if (room >= budget) return budget      // early out; also avoids Infinity math
  }
  return Math.min(budget, room)
}

export function armyHeadroom(t: CappedTerritory): number {
  if (typeof t.maxArmies !== 'number') return Infinity
  return Math.max(0, t.maxArmies - t.armies)
}

// ─── one-way borders ──────────────────────────────────────────────────────────

/**
 * Every territory this one borders in the OUTGOING direction: its two-way
 * borders (`adjacencies`) plus any one-way borders (`oneWayBorders`).
 *
 * A one-way border is a real border that only works in one direction. It is
 * usable for BOTH attack and fortify, exactly like a two-way border — the only
 * difference is that the far side has no matching edge back, so it can neither
 * attack nor fortify into the source. Capture the Flag's watchtowers hold their
 * whole compound this way. A territory can have both kinds at once: a tower
 * keeps its ordinary borders and gains directed ones on top.
 *
 * `adjacencies` stays symmetric, which is what lets the symmetry,
 * self-adjacency and connectivity invariants in
 * apps/api/tests/engine/maps.test.ts stay strict for every map. Borders default
 * to two-way: `oneWayBorders` is optional and absent on every other map.
 */
export function borderReach(from: BorderReachTerritory): string[] {
  if (!from.oneWayBorders?.length) return from.adjacencies
  return [...new Set([...from.adjacencies, ...from.oneWayBorders])]
}

/** Whether `from` borders `toId` in the outgoing direction (two-way OR one-way). */
export function hasBorderTo(from: BorderReachTerritory, toId: string): boolean {
  return from.adjacencies.includes(toId) || (from.oneWayBorders?.includes(toId) ?? false)
}

function sidesOf(spec: BorderDice | undefined, mode: 'attack' | 'defend'): number {
  if (typeof spec === 'number') return spec >= 2 ? spec : DEFAULT_DICE_SIDES
  const n = spec?.[mode]
  return typeof n === 'number' && n >= 2 ? n : DEFAULT_DICE_SIDES
}

/**
 * Sides on `from`'s dice when it ATTACKS across the border to `toId`.
 *
 * Per SIDE and per MODE: each endpoint carries its own values, so a tower can
 * attack at d8 while the territory opposite still rolls d6 back, and a fort can
 * defend at d7 without attacking at d7.
 */
export function attackDiceSides(from: BorderReachTerritory, toId: string): number {
  return sidesOf(from.borderDice?.[toId], 'attack')
}

/** Sides on `from`'s dice when it DEFENDS against an attack coming from `toId`. */
export function defendDiceSides(from: BorderReachTerritory, toId: string): number {
  return sidesOf(from.borderDice?.[toId], 'defend')
}

// ─── reinforcementCount ───────────────────────────────────────────────────────

/**
 * Calculate how many new armies a player receives at the start of their turn.
 *
 * Formula:
 *   count = max(3, floor(territoriesOwned / 3))
 *           + sum(region.bonus for each region the player fully owns)
 *           + bonusArmies  (card turn-ins + eliminate-player bonuses)
 *
 * The classic Risk minimum on the territory base is 3.
 *
 * Eliminated players (zero territories owned) draw 0 — the floor doesn't
 * apply, and the pending bonus pool is dropped because there's nowhere to
 * spend it.  Without this guard a caller that forgets to filter eliminated
 * players out of a turn loop would hand them 3 armies they cannot place.
 *
 * @param ownedTerritories  All territories currently owned by this player.
 *   Pass only the territories this player owns (filter upstream).
 *   Passing an empty array signals elimination and returns 0.
 * @param regions           All region definitions for the map (with bonuses).
 * @param allTerritories    All territories on the map (needed to check full
 *   region ownership — a region bonus only applies when the player owns
 *   EVERY territory in it).
 * @param bonusArmies       Pending bonus armies from card turn-ins and
 *   eliminate-player bonuses for this player.  Pass 0 when absent.
 */
export function reinforcementCount(
  ownedTerritories: readonly ReinforcementTerritory[],
  regions: readonly ReinforcementRegion[],
  allTerritories: readonly ReinforcementTerritory[],
  bonusArmies: number,
  playerId: string,
): number {
  // Eliminated players (zero territories) draw NO reinforcements — not even
  // the max(3, …) base.
  if (ownedTerritories.length === 0) return 0

  const base = Math.max(3, Math.floor(ownedTerritories.length / 3))

  // Continent (region) bonuses: add bonus only when the player owns every
  // territory in the region.
  let regionBonus = 0
  for (const region of regions) {
    const allOwned = region.territoryIds.every((tid) => {
      const territory = allTerritories.find((t) => t.id === tid)
      return territory !== undefined && territory.ownerId === playerId
    })
    if (allOwned) {
      regionBonus += region.bonus
    }
  }

  return base + regionBonus + bonusArmies
}

// ─── isValidSet ───────────────────────────────────────────────────────────────

/**
 * Validate that `cards` (exactly 3) forms a legal turn-in set:
 *   - three of the same type (3 infantry / cavalry / artillery), OR
 *   - one of each type, OR
 *   - any of the above with wilds substituting for any type.
 *
 * Wilds can fill any required slot.  Two wilds + any one type = valid
 * three-of-a-kind by substitution; three wilds is also accepted (rare but
 * legal under standard rules).
 */
export function isValidSet(cards: readonly Card[]): boolean {
  if (cards.length !== 3) return false
  const wilds = cards.filter((c) => c.type === 'wild').length
  const nonWild = cards.filter((c) => c.type !== 'wild')
  const types = new Set(nonWild.map((c) => c.type))

  // 3 wilds — trivially satisfies any pattern.
  if (wilds === 3) return true
  // 2 wilds + any single non-wild → fills three-of-a-kind by substitution.
  if (wilds === 2 && nonWild.length === 1) return true
  // 1 wild: either two-of-a-kind + wild (3-of-a-kind), or two different types
  // + wild (one-of-each).
  if (wilds === 1 && nonWild.length === 2) {
    if (types.size === 1) return true // wild + pair → 3-of-a-kind
    if (types.size === 2) return true // wild + two different → one-of-each (third type subbed)
    return false
  }
  // 0 wilds: classic 3-of-a-kind OR one-of-each.
  if (wilds === 0 && nonWild.length === 3) {
    if (types.size === 1) return true
    if (types.size === 3) return true
    return false
  }
  return false
}

// ─── scheduleBonus ────────────────────────────────────────────────────────────

/**
 * Compute the bonus armies awarded for the Nth set turn-in (zero-indexed in
 * `cardTurnIns`).  The state's cardTurnIns counter is the pre-increment value
 * (the count of turn-ins BEFORE this one).
 *
 * `cycle`       — bonuses cycle [4, 6, 8, 10] indexed by cardTurnIns mod 4.
 *                 Warfish-style fixed-set vibe (modest, never escalates).
 * `incremental` — bonus = 4 + cardTurnIns.  Classic-style escalation
 *                 without the 4/6/8/10/12/15/+5 plateau bump.
 * `fixed`       — always 5.
 * `climb`       — 3, 6, 9, 12… (3 × the set's number).
 * `surge`       — 5, 10, 15, 20, 25… (5 × the set's number).
 */
export function scheduleBonus(cardTurnIns: number, schedule: CardBonusSchedule): number {
  switch (schedule) {
    case 'incremental': return 4 + cardTurnIns
    case 'fixed': return 5
    case 'climb': return 3 * (cardTurnIns + 1)
    case 'surge': return 5 * (cardTurnIns + 1)
    default: {
      // 'cycle' (default)
      const table = [4, 6, 8, 10]
      return table[cardTurnIns % 4] as number
    }
  }
}
